import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { Loader2, Upload, Users, Calendar, Package, DollarSign, Plus, Pencil, Trash2, UserCheck, Save, X, Search, Newspaper, Mail, Plug, User, Briefcase, CheckCircle2, RotateCcw, ArrowRight, MessageSquare, BarChart3, Link2, GraduationCap, Gift } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import Reveal from "@/components/Reveal";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ProductFormDialog from "@/components/ProductFormDialog";
import BlogManager from "@/components/admin/BlogManager";
import SubscribersTable from "@/components/admin/SubscribersTable";
import IntegrationsPanel from "@/components/admin/IntegrationsPanel";
import FoundersManager from "@/components/admin/FoundersManager";
import LessonsManager from "@/components/admin/LessonsManager";
import AnalyticsPanel from "@/components/admin/AnalyticsPanel";
import DirectorySubmissions from "@/components/admin/DirectorySubmissions";

const TABS = [
  { key: "leads", label: "Leads", icon: <Users size={14} /> },
  { key: "clients", label: "Clients", icon: <Briefcase size={14} /> },
  { key: "bookings", label: "Bookings", icon: <Calendar size={14} /> },
  { key: "members", label: "Members", icon: <UserCheck size={14} /> },
  { key: "products", label: "Products & Uploads", icon: <Package size={14} /> },
  { key: "analytics", label: "LMS Analytics", icon: <BarChart3 size={14} /> },
  { key: "directories", label: "Directories", icon: <Link2 size={14} /> },
  { key: "transactions", label: "Transactions", icon: <DollarSign size={14} /> },
  { key: "blog", label: "Insights", icon: <Newspaper size={14} /> },
  { key: "newsletter", label: "Subscribers", icon: <Mail size={14} /> },
  { key: "founders", label: "Founders", icon: <User size={14} /> },
  { key: "integrations", label: "Integrations", icon: <Plug size={14} /> },
];

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState(() => {
    // Preserve tab across OAuth callback ?tab=integrations
    const p = new URLSearchParams(window.location.search);
    return p.get("tab") || "leads";
  });
  const [summary, setSummary] = useState(null);
  const [data, setData] = useState({ leads: [], bookings: [], products: [], transactions: [], members: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && user !== false && user.role === "admin") {
      (async () => {
        try {
          const [s, l, b, p, t, m] = await Promise.all([
            api.get("/admin/summary"),
            api.get("/admin/leads"),
            api.get("/admin/bookings"),
            api.get("/admin/products"),
            api.get("/admin/transactions"),
            api.get("/admin/members"),
          ]);
          setSummary(s.data);
          setData({ leads: l.data, bookings: b.data, products: p.data, transactions: t.data, members: m.data });
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [user]);

  const refreshProducts = async () => {
    const { data: p } = await api.get("/admin/products");
    setData((d) => ({ ...d, products: p }));
  };

  const refreshMembers = async () => {
    const { data: m } = await api.get("/admin/members");
    setData((d) => ({ ...d, members: m }));
  };
  const refreshLeads = async () => {
    const { data: l } = await api.get("/admin/leads");
    setData((d) => ({ ...d, leads: l }));
  };
  const refreshBookings = async () => {
    const { data: b } = await api.get("/admin/bookings");
    setData((d) => ({ ...d, bookings: b }));
  };

  if (user === null) {
    return (
      <SiteLayout branch="main">
        <div className="section text-center text-[var(--muted)]">Loading…</div>
      </SiteLayout>
    );
  }
  if (user === false) return <Navigate to="/admin/login" replace />;
  if (user.role !== "admin") {
    return (
      <SiteLayout branch="main">
        <div className="section text-center">
          <h2 className="font-display text-3xl text-[var(--navy)]">Admin access only</h2>
          <p className="text-[var(--muted)] mt-4">You don't have permission to view this page.</p>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout branch="main">
      <section className="section bg-[var(--light)] !py-12">
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <span className="eyebrow-dark">ADMIN CONSOLE</span>
            <h1 className="font-display text-4xl text-[var(--navy)] mt-3">Operations Dashboard</h1>
          </Reveal>

          {/* SUMMARY */}
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mt-8" data-testid="admin-summary">
              <SumTile label="Leads" value={summary.leads} />
              <SumTile label="Bookings" value={summary.bookings} />
              <SumTile label="Users" value={summary.users} />
              <SumTile label="Products" value={summary.products} />
              <SumTile label="Paid" value={summary.paid_transactions} />
              <SumTile label="Revenue" value={`$${summary.revenue_usd.toLocaleString()}`} highlight />
            </div>
          )}

          {/* TABS */}
          <div className="flex flex-wrap gap-2 mt-10 border-b border-[var(--line)]" data-testid="admin-tabs">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-2 px-5 py-3 text-[11px] font-bold tracking-widest transition-colors border-b-2 -mb-px ${
                  tab === t.key
                    ? "text-[var(--navy)] border-[var(--gold)]"
                    : "text-[var(--muted)] border-transparent hover:text-[var(--navy)]"
                }`}
                data-testid={`tab-${t.key}`}
              >
                {t.icon} {t.label.toUpperCase()}
              </button>
            ))}
          </div>

          {/* CONTENT */}
          <div className="mt-8">
            {loading ? (
              <div className="text-[var(--muted)] text-sm">Loading…</div>
            ) : (
              <>
                {tab === "leads" && <LeadsTable rows={data.leads} onChanged={refreshLeads} />}
                {tab === "clients" && <ClientsTab rows={data.leads} onChanged={refreshLeads} />}
                {tab === "bookings" && <BookingsTable rows={data.bookings} onChanged={refreshBookings} />}
                {tab === "members" && <MembersTable rows={data.members} products={data.products} onUpdated={refreshMembers} />}
                {tab === "products" && <ProductsTable rows={data.products} onUploaded={refreshProducts} />}
                {tab === "analytics" && <AnalyticsPanel />}
                {tab === "directories" && <DirectorySubmissions />}
                {tab === "transactions" && <TxTable rows={data.transactions} />}
                {tab === "blog" && <BlogManager />}
                {tab === "newsletter" && <SubscribersTable />}
                {tab === "founders" && <FoundersManager />}
                {tab === "integrations" && <IntegrationsPanel />}
              </>
            )}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

function SumTile({ label, value, highlight }) {
  return (
    <div className={`p-5 border ${highlight ? "bg-[var(--navy)] text-white border-[var(--navy)]" : "bg-white border-[var(--line)]"}`}>
      <div className={`text-[10px] tracking-widest ${highlight ? "text-[var(--gold)]" : "text-[var(--muted)]"} font-bold`}>{label.toUpperCase()}</div>
      <div className="font-display text-2xl mt-2">{value}</div>
    </div>
  );
}

function EmptyState({ label }) {
  return <div className="text-center py-16 text-[var(--muted)] text-sm">No {label} yet.</div>;
}

function LeadsTable({ rows, onChanged }) {
  const [converting, setConverting] = useState(null);
  // Hide already-converted leads by default (they appear in the Clients tab)
  const visibleRows = rows.filter((r) => (r.status || "lead") === "lead");
  if (!visibleRows.length) return <EmptyState label="active leads" />;

  const convert = async (id) => {
    setConverting(id);
    try {
      await api.patch(`/admin/leads/${id}`, { status: "client" });
      onChanged && onChanged();
    } catch (e) {
      alert(formatApiError(e));
    } finally {
      setConverting(null);
    }
  };

  return (
    <div className="border border-[var(--line)] bg-white overflow-x-auto" data-testid="leads-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[var(--navy)] text-white text-left">
            {["Date", "Name", "Email", "Organization", "Interest", "Message", "Source", ""].map((h) => (
              <th key={h} className="px-4 py-3 text-[10px] tracking-widest uppercase font-bold">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((r) => (
            <tr key={r.id} className="border-t border-[var(--line)] hover:bg-[var(--cream)]" data-testid={`lead-row-${r.id}`}>
              <td className="px-4 py-3 text-[var(--muted)] text-xs whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
              <td className="px-4 py-3 font-bold text-[var(--navy)]">{r.name}</td>
              <td className="px-4 py-3">{r.email}</td>
              <td className="px-4 py-3 text-[var(--muted)]">{r.organization || "—"}</td>
              <td className="px-4 py-3"><span className="text-[10px] tracking-widest bg-[var(--cream)] px-2 py-1">{(r.interest || "—").toUpperCase()}</span></td>
              <td className="px-4 py-3 max-w-md text-xs text-[var(--muted)] whitespace-pre-wrap">{r.message}</td>
              <td className="px-4 py-3 text-[var(--muted)] text-xs">{r.source}</td>
              <td className="px-4 py-3 text-right">
                <button
                  onClick={() => convert(r.id)}
                  disabled={converting === r.id}
                  className="btn btn-navy !py-1.5 !px-3 !text-[10px] disabled:opacity-60"
                  data-testid={`convert-client-${r.id}`}
                  title="Convert this lead to a client"
                >
                  {converting === r.id ? <Loader2 className="animate-spin mr-1" size={10} /> : <ArrowRight size={10} className="mr-1" />} Convert to Client
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ClientsTab({ rows, onChanged }) {
  const clientRows = rows.filter((r) => r.status === "client");
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState({});
  const [reverting, setReverting] = useState(null);

  const toggle = async (email) => {
    if (expanded === email) {
      setExpanded(null);
      return;
    }
    setExpanded(email);
    if (!detail[email]) {
      try {
        const { data } = await api.get(`/admin/clients/${encodeURIComponent(email)}`);
        setDetail((d) => ({ ...d, [email]: data }));
      } catch (e) {
        setDetail((d) => ({ ...d, [email]: { error: formatApiError(e) } }));
      }
    }
  };

  const revert = async (id) => {
    setReverting(id);
    try {
      await api.patch(`/admin/leads/${id}`, { status: "lead" });
      onChanged && onChanged();
      setExpanded(null);
    } catch (e) {
      alert(formatApiError(e));
    } finally {
      setReverting(null);
    }
  };

  if (!clientRows.length) return <EmptyState label="clients — convert a lead to get started" />;

  return (
    <div className="border border-[var(--line)] bg-white overflow-x-auto" data-testid="clients-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[var(--navy)] text-white text-left">
            {["Converted", "Name", "Email", "Organization", "Interest", ""].map((h) => (
              <th key={h} className="px-4 py-3 text-[10px] tracking-widest uppercase font-bold">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {clientRows.map((r) => (
            <Fragment key={r.id}>
              <tr
                className="border-t border-[var(--line)] hover:bg-[var(--cream)] cursor-pointer"
                onClick={() => toggle(r.email)}
                data-testid={`client-row-${r.id}`}
              >
                <td className="px-4 py-3 text-[var(--muted)] text-xs whitespace-nowrap">
                  {r.converted_at ? new Date(r.converted_at).toLocaleString() : "—"}
                </td>
                <td className="px-4 py-3 font-bold text-[var(--navy)]">{r.name}</td>
                <td className="px-4 py-3">{r.email}</td>
                <td className="px-4 py-3 text-[var(--muted)]">{r.organization || "—"}</td>
                <td className="px-4 py-3"><span className="text-[10px] tracking-widest bg-[var(--gold)]/20 text-[var(--navy)] px-2 py-1">{(r.interest || "—").toUpperCase()}</span></td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={(e) => { e.stopPropagation(); revert(r.id); }}
                    disabled={reverting === r.id}
                    className="btn btn-ghost !py-1.5 !px-3 !text-[10px] disabled:opacity-60"
                    data-testid={`revert-lead-${r.id}`}
                    title="Move back to Leads"
                  >
                    {reverting === r.id ? <Loader2 className="animate-spin mr-1" size={10} /> : <RotateCcw size={10} className="mr-1" />} Move back
                  </button>
                </td>
              </tr>
              {expanded === r.email && (
                <tr>
                  <td colSpan={6} className="bg-[var(--cream)] px-6 py-5 border-t border-[var(--line)]" data-testid={`client-detail-${r.id}`}>
                    <ClientDetail data={detail[r.email]} />
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ClientDetail({ data }) {
  if (!data) return <div className="text-sm text-[var(--muted)]"><Loader2 className="inline animate-spin mr-2" size={12} /> Loading…</div>;
  if (data.error) return <div className="text-sm text-red-700">{data.error}</div>;
  const { bookings = [], transactions = [], chats = [] } = data;
  return (
    <div className="grid md:grid-cols-3 gap-6 text-sm">
      <DetailBlock title="Bookings" count={bookings.length} icon={<Calendar size={12} />}>
        {bookings.length === 0 ? (
          <div className="text-xs text-[var(--muted)]">No bookings on file</div>
        ) : bookings.map((b) => (
          <div key={b.id} className="py-1.5 border-b border-[var(--line)] last:border-0">
            <div className="font-bold text-[var(--navy)] text-xs">{b.slot_date} · {b.slot_time}</div>
            <div className="text-[11px] text-[var(--muted)]">{b.topic} {b.completed_at && <span className="text-green-700 font-bold ml-1">· COMPLETED</span>}</div>
          </div>
        ))}
      </DetailBlock>
      <DetailBlock title="Purchases" count={transactions.length} icon={<DollarSign size={12} />}>
        {transactions.length === 0 ? (
          <div className="text-xs text-[var(--muted)]">No purchases on file</div>
        ) : transactions.map((t) => (
          <div key={t.id || t.session_id} className="py-1.5 border-b border-[var(--line)] last:border-0">
            <div className="font-bold text-[var(--navy)] text-xs">{t.product_name}</div>
            <div className="text-[11px] text-[var(--muted)]">${Number(t.amount||0).toFixed(2)} · {t.payment_status}</div>
          </div>
        ))}
      </DetailBlock>
      <DetailBlock title="Chats" count={chats.length} icon={<MessageSquare size={12} />}>
        {chats.length === 0 ? (
          <div className="text-xs text-[var(--muted)]">No chat sessions</div>
        ) : chats.map((c) => (
          <div key={c.id || c.session_id} className="py-1.5 border-b border-[var(--line)] last:border-0">
            <div className="font-bold text-[var(--navy)] text-xs">Session {(c.session_id || "").slice(0, 12)}…</div>
            <div className="text-[11px] text-[var(--muted)]">{(c.messages || []).length} messages · updated {new Date(c.updated_at).toLocaleString()}</div>
          </div>
        ))}
      </DetailBlock>
    </div>
  );
}

function DetailBlock({ title, count, icon, children }) {
  return (
    <div className="bg-white border border-[var(--line)] p-4">
      <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-[var(--muted)] flex items-center gap-1.5 mb-3">
        {icon} {title} <span className="text-[var(--gold)]">({count})</span>
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function BookingsTable({ rows, onChanged }) {
  const [filter, setFilter] = useState("all");
  const [toggling, setToggling] = useState(null);
  if (!rows.length) return <EmptyState label="bookings" />;

  const filtered = rows.filter((r) => {
    if (filter === "all") return true;
    if (filter === "completed") return !!r.completed_at;
    if (filter === "open") return !r.completed_at;
    return true;
  });

  const toggle = async (id, isDone) => {
    setToggling(id);
    try {
      await api.patch(`/admin/bookings/${id}`, { status: isDone ? "open" : "complete" });
      onChanged && onChanged();
    } catch (e) {
      alert(formatApiError(e));
    } finally {
      setToggling(null);
    }
  };

  const chip = (val, label) => (
    <button
      onClick={() => setFilter(val)}
      className={`text-[11px] tracking-widest uppercase font-bold px-3 py-1.5 border transition-colors ${
        filter === val
          ? "bg-[var(--navy)] text-white border-[var(--navy)]"
          : "bg-white text-[var(--navy)] border-[var(--line)] hover:border-[var(--navy)]"
      }`}
      data-testid={`bookings-filter-${val}`}
    >
      {label}
    </button>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {chip("all", `All (${rows.length})`)}
        {chip("open", `Upcoming (${rows.filter((r) => !r.completed_at).length})`)}
        {chip("completed", `Completed (${rows.filter((r) => r.completed_at).length})`)}
      </div>
      <div className="border border-[var(--line)] bg-white overflow-x-auto" data-testid="bookings-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[var(--navy)] text-white text-left">
              {["Slot", "Booked at", "Name", "Email", "Topic", "Notes", "Status", ""].map((h) => (
                <th key={h} className="px-4 py-3 text-[10px] tracking-widest uppercase font-bold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const done = !!r.completed_at;
              return (
                <tr
                  key={r.id}
                  className={`border-t border-[var(--line)] hover:bg-[var(--cream)] ${done ? "opacity-60" : ""}`}
                  data-testid={`booking-row-${r.id}`}
                >
                  <td className={`px-4 py-3 font-bold whitespace-nowrap ${done ? "line-through text-[var(--muted)]" : "text-[var(--navy)]"}`}>{r.slot_date} · {r.slot_time}</td>
                  <td className="px-4 py-3 text-[var(--muted)] text-xs whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3">{r.name}</td>
                  <td className="px-4 py-3">{r.email}</td>
                  <td className="px-4 py-3"><span className="text-[10px] tracking-widest bg-[var(--cream)] px-2 py-1">{(r.topic || "—").toUpperCase()}</span></td>
                  <td className="px-4 py-3 max-w-xs text-xs text-[var(--muted)] whitespace-pre-wrap">{r.notes || "—"}</td>
                  <td className="px-4 py-3 text-xs">
                    {done ? (
                      <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 px-2 py-1 text-[10px] tracking-widest font-bold" data-testid={`booking-status-${r.id}`}>
                        <CheckCircle2 size={10} /> COMPLETED
                      </span>
                    ) : (
                      <span className="text-[10px] tracking-widest text-[var(--muted)] font-bold">{(r.status || "new").toUpperCase()}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => toggle(r.id, done)}
                      disabled={toggling === r.id}
                      className={`btn !py-1.5 !px-3 !text-[10px] disabled:opacity-60 ${done ? "btn-ghost" : "btn-navy"}`}
                      data-testid={`booking-toggle-${r.id}`}
                    >
                      {toggling === r.id
                        ? <Loader2 className="animate-spin mr-1" size={10} />
                        : done ? <RotateCcw size={10} className="mr-1" /> : <CheckCircle2 size={10} className="mr-1" />}
                      {done ? "Reopen" : "Mark Complete"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProductsTable({ rows, onUploaded }) {
  const [dialog, setDialog] = useState({ open: false, mode: "create", product: null });
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState("");

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    setDeleting(p.id);
    setError("");
    try {
      await api.delete(`/admin/products/${p.id}`);
      onUploaded && onUploaded();
    } catch (e) {
      setError(`${p.name}: ${formatApiError(e)}`);
    } finally {
      setDeleting(null);
    }
  };

  const grouped = rows.reduce((acc, p) => {
    (acc[p.category] = acc[p.category] || []).push(p);
    return acc;
  }, {});
  const order = ["Digital Downloads", "Toolkits", "Courses", "Memberships"];

  return (
    <div data-testid="products-admin">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-display text-xl text-[var(--navy)]">Catalog</h3>
          <p className="text-xs text-[var(--muted)]">{rows.length} products across {Object.keys(grouped).length} categories</p>
        </div>
        <button
          onClick={() => setDialog({ open: true, mode: "create", product: null })}
          className="btn btn-navy !py-2 !px-4 !text-[11px]"
          data-testid="add-product-btn"
        >
          <Plus size={14} className="mr-2" /> Add Product
        </button>
      </div>

      {error && <p className="text-red-700 text-xs mb-4" data-testid="products-error">{error}</p>}

      {order.filter((cat) => grouped[cat]?.length).map((cat) => (
        <div key={cat} className="mb-8" data-testid={`cat-group-${cat.replace(/\s+/g, "-")}`}>
          <div className="flex items-baseline justify-between mb-3">
            <h4 className="text-[10px] tracking-widest font-bold text-[var(--gold)]">{cat.toUpperCase()}</h4>
            <span className="text-[10px] text-[var(--muted)]">{grouped[cat].length} item{grouped[cat].length === 1 ? "" : "s"}</span>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {grouped[cat].map((p) => (
              <ProductAdminCard
                key={p.id}
                product={p}
                onUploaded={onUploaded}
                onEdit={() => setDialog({ open: true, mode: "edit", product: p })}
                onDelete={() => handleDelete(p)}
                deleting={deleting === p.id}
              />
            ))}
          </div>
        </div>
      ))}

      <ProductFormDialog
        open={dialog.open}
        mode={dialog.mode}
        product={dialog.product}
        onClose={() => setDialog({ open: false, mode: "create", product: null })}
        onSaved={() => onUploaded && onUploaded()}
      />
    </div>
  );
}

function ProductAdminCard({ product, onUploaded, onEdit, onDelete, deleting }) {
  const inputRef = useRef();
  const riseInputRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState("");
  const [riseUploading, setRiseUploading] = useState(false);
  const [riseMsg, setRiseMsg] = useState("");
  const [showLessons, setShowLessons] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    setMsg("");
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post(`/admin/products/${product.id}/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMsg("Uploaded ✓");
      onUploaded && onUploaded();
    } catch (e) {
      setMsg(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const uploadRiseZip = async (file) => {
    if (!file) return;
    setRiseUploading(true);
    setRiseMsg("");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/products/${product.id}/upload-course`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      setRiseMsg(`✓ ${data.uploaded} files extracted${data.skipped ? ` (${data.skipped} skipped)` : ""}`);
      onUploaded && onUploaded();
    } catch (e) {
      setRiseMsg(`✗ ${formatApiError(e)}`);
    } finally {
      setRiseUploading(false);
    }
  };

  const uploadable = product.type === "download" || product.type === "toolkit";

  return (
    <div className="border border-[var(--line)] bg-white p-5 flex flex-col" data-testid={`padmin-${product.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] tracking-widest text-[var(--gold)] font-bold">
            {product.category.toUpperCase()}
          </div>
          <div className="font-display text-lg text-[var(--navy)] mt-2 leading-tight break-words">{product.name}</div>
          <div className="text-xs text-[var(--muted)] mt-1">${product.price} · <code className="text-[10px]">{product.id}</code></div>
        </div>
        <div className="flex flex-col gap-1 flex-shrink-0">
          <button
            onClick={onEdit}
            className="w-7 h-7 border border-[var(--line)] hover:border-[var(--navy)] flex items-center justify-center"
            title="Edit"
            data-testid={`edit-${product.id}`}
          >
            <Pencil size={12} className="text-[var(--navy)]" />
          </button>
          <button
            onClick={onDelete}
            disabled={deleting}
            className="w-7 h-7 border border-[var(--line)] hover:border-red-500 hover:text-red-500 flex items-center justify-center disabled:opacity-50"
            title="Delete"
            data-testid={`delete-${product.id}`}
          >
            {deleting ? <Loader2 className="animate-spin" size={12} /> : <Trash2 size={12} />}
          </button>
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-1 text-xs">
        <span className={product.has_file ? "text-green-700" : "text-neutral-400"}>
          {product.has_file ? "✓ File uploaded" : uploadable ? "✗ No file" : "— No file needed"}
        </span>
        {product.type === "membership" && (
          <span className={product.has_stripe_price ? "text-green-700" : "text-red-700"}>
            {product.has_stripe_price ? "✓ Stripe price ready" : "✗ Stripe price missing"}
          </span>
        )}
      </div>

      {uploadable && (
        <div className="mt-4">
          <input
            ref={inputRef}
            type="file"
            accept=".pdf,.docx,.zip,.xlsx"
            className="hidden"
            onChange={(e) => upload(e.target.files?.[0])}
            data-testid={`upload-input-${product.id}`}
          />
          <button
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="btn btn-ghost w-full !py-2 !text-[11px] justify-center"
            data-testid={`upload-btn-${product.id}`}
          >
            {uploading ? <><Loader2 className="animate-spin mr-2" size={12} /> Uploading…</> : <><Upload size={12} className="mr-2" /> {product.has_file ? "Replace file" : "Upload file"}</>}
          </button>
          {msg && <p className="text-xs mt-2 text-[var(--muted)]">{msg}</p>}
        </div>
      )}
      {product.type === "course" && (
        <div className="mt-3 space-y-2">
          <input
            ref={riseInputRef}
            type="file"
            accept=".zip,application/zip"
            className="hidden"
            onChange={(e) => { uploadRiseZip(e.target.files?.[0]); e.target.value = ""; }}
            data-testid={`rise-input-${product.id}`}
          />
          <button
            onClick={() => riseInputRef.current?.click()}
            disabled={riseUploading}
            className="btn btn-ghost w-full !py-2 !text-[11px] justify-center"
            data-testid={`rise-upload-${product.id}`}
            title="Rise / Articulate export .zip — served on /course/<id>"
          >
            {riseUploading
              ? <><Loader2 className="animate-spin mr-2" size={12} /> Extracting…</>
              : <><Upload size={12} className="mr-2" /> {product.course_uploaded_at ? `Replace Rise zip · ${product.course_file_count || 0} files` : "Upload Rise zip (Articulate/Rise 360 export)"}</>}
          </button>
          {riseMsg && (
            <p className={`text-[11px] ${riseMsg.startsWith("✓") ? "text-emerald-700" : "text-red-600"}`}
               data-testid={`rise-msg-${product.id}`}>{riseMsg}</p>
          )}
          <button
            onClick={() => setShowLessons((v) => !v)}
            className="btn btn-ghost w-full !py-2 !text-[11px] justify-center"
            data-testid={`lessons-toggle-${product.id}`}
          >
            {showLessons ? "Hide lessons ×" : "Manage lessons →"}
          </button>
        </div>
      )}
      {showLessons && product.type === "course" && (
        <div className="mt-3 -mx-5 -mb-5">
          <LessonsManager productId={product.id} onClose={() => setShowLessons(false)} />
        </div>
      )}
    </div>
  );
}

function TxTable({ rows }) {
  if (!rows.length) return <EmptyState label="transactions" />;
  return (
    <div className="border border-[var(--line)] bg-white overflow-x-auto" data-testid="tx-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[var(--navy)] text-white text-left">
            {["Created", "User", "Product", "Type", "Mode", "Amount", "Payment", "Status"].map((h) => (
              <th key={h} className="px-4 py-3 text-[10px] tracking-widest uppercase font-bold">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.session_id} className="border-t border-[var(--line)] hover:bg-[var(--cream)]">
              <td className="px-4 py-3 text-[var(--muted)] text-xs whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
              <td className="px-4 py-3">{r.user_email || "guest"}</td>
              <td className="px-4 py-3">{r.product_name}</td>
              <td className="px-4 py-3 text-xs">{r.product_type}</td>
              <td className="px-4 py-3 text-xs">{r.mode || "one_time"}</td>
              <td className="px-4 py-3 font-bold">${r.amount}</td>
              <td className="px-4 py-3 text-xs">
                <span className={`px-2 py-1 ${r.payment_status === "paid" ? "bg-green-100 text-green-800" : "bg-neutral-100 text-neutral-600"}`}>
                  {r.payment_status}
                </span>
              </td>
              <td className="px-4 py-3 text-xs">{r.status}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MembersTable({ rows, products = [], onUpdated }) {
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // { id, name, organization }
  const [saving, setSaving] = useState(false);
  const [granting, setGranting] = useState(null); // { email, name }
  const [grants, setGrants] = useState({}); // email -> [tx]
  const [adding, setAdding] = useState(false);

  const filtered = q
    ? rows.filter((m) => {
        const t = q.toLowerCase();
        return (
          (m.name || "").toLowerCase().includes(t) ||
          (m.email || "").toLowerCase().includes(t) ||
          (m.organization || "").toLowerCase().includes(t)
        );
      })
    : rows;

  const startEdit = (m) => setEditing({ id: m.id, name: m.name || "", organization: m.organization || "" });
  const cancel = () => setEditing(null);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/members/${editing.id}`, {
        name: editing.name,
        organization: editing.organization,
      });
      setEditing(null);
      onUpdated && onUpdated();
    } finally {
      setSaving(false);
    }
  };

  const loadGrants = async (email) => {
    try {
      const { data } = await api.get(`/admin/members/${encodeURIComponent(email)}/grants`);
      setGrants((g) => ({ ...g, [email]: data }));
    } catch (e) {
      setGrants((g) => ({ ...g, [email]: [] }));
    }
  };

  if (!rows.length) return (
    <div className="space-y-4" data-testid="members-table">
      <div className="flex justify-end">
        <button
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--navy)] text-white text-xs font-bold uppercase tracking-widest hover:bg-[var(--navy-dark,#0c2035)]"
          data-testid="add-member-btn"
        >
          <Plus size={13} /> Add Member
        </button>
      </div>
      <div className="text-center py-16 text-[var(--muted)] text-sm">No members yet.</div>
      {adding && <AddMemberDialog onClose={() => setAdding(false)} onCreated={() => { setAdding(false); onUpdated && onUpdated(); }} />}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="members-table">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
          <input
            placeholder="Search name, email, or organization…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full !pl-9 !py-2 !text-sm border border-[var(--line)] bg-white"
            data-testid="member-search"
          />
        </div>
        <span className="text-xs text-[var(--muted)]">{filtered.length} of {rows.length}</span>
        <button
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-2 px-3 py-2 bg-[var(--navy)] text-white text-[11px] font-bold uppercase tracking-widest hover:bg-[var(--navy-dark,#0c2035)]"
          data-testid="add-member-btn"
        >
          <Plus size={12} /> Add Member
        </button>
      </div>

      <div className="border border-[var(--line)] bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[var(--navy)] text-white text-left">
              {["Joined", "Name", "Email", "Organization", "Role", "Membership", "Purchases", "Spend", ""].map((h) => (
                <th key={h} className="px-4 py-3 text-[10px] tracking-widest uppercase font-bold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>            {filtered.map((m) => {
              const isEditing = editing?.id === m.id;
              return (
                <tr key={m.id} className="border-t border-[var(--line)] hover:bg-[var(--cream)]" data-testid={`member-row-${m.id}`}>
                  <td className="px-4 py-3 text-[var(--muted)] text-xs whitespace-nowrap">{new Date(m.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3">
                    {isEditing ? (
                      <input
                        value={editing.name}
                        onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                        className="!py-1 !text-sm"
                        data-testid={`edit-name-${m.id}`}
                      />
                    ) : (
                      <span className="font-bold text-[var(--navy)]">{m.name}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">{m.email}</td>
                  <td className="px-4 py-3">
                    {isEditing ? (
                      <input
                        value={editing.organization}
                        onChange={(e) => setEditing({ ...editing, organization: e.target.value })}
                        className="!py-1 !text-sm"
                        data-testid={`edit-org-${m.id}`}
                      />
                    ) : m.organization ? (
                      <span className="text-[10px] tracking-widest bg-[var(--cream)] px-2 py-1">{m.organization}</span>
                    ) : (
                      <span className="text-[var(--muted)]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {m.role === "admin"
                      ? <span className="text-[10px] tracking-widest bg-[var(--navy)] text-white px-2 py-1">ADMIN</span>
                      : "member"}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <div className="flex flex-col gap-1">
                      {m.membership_status === "active"
                        ? <span className="text-green-700 font-bold">ACTIVE</span>
                        : <span className="text-[var(--muted)]">—</span>}
                      {m.continuum_comp && (
                        <span
                          className="inline-flex items-center gap-1 text-[9px] tracking-widest bg-[var(--gold)] text-white px-2 py-0.5 font-bold w-fit"
                          title={`Comp'd Continuum${m.continuum_comp_by ? " by " + m.continuum_comp_by : ""}`}
                          data-testid={`continuum-badge-${m.id}`}
                        >
                          <GraduationCap size={9} /> CONTINUUM
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">{m.purchases_count}</td>
                  <td className="px-4 py-3 font-bold">${m.total_spend.toFixed(2)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {isEditing ? (
                      <div className="flex gap-1">
                        <button onClick={save} disabled={saving} className="p-1 border border-[var(--line)] hover:border-green-500 hover:text-green-600" title="Save" data-testid={`save-${m.id}`}>
                          {saving ? <Loader2 className="animate-spin" size={12} /> : <Save size={12} />}
                        </button>
                        <button onClick={cancel} className="p-1 border border-[var(--line)] hover:border-red-500 hover:text-red-600" title="Cancel" data-testid={`cancel-edit-${m.id}`}>
                          <X size={12} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-1">
                        <button
                          onClick={() => { setGranting({ email: m.email, name: m.name }); loadGrants(m.email); }}
                          className="inline-flex items-center gap-1 px-2 py-1 border border-[var(--gold)] text-[var(--navy)] text-[10px] tracking-widest uppercase font-bold hover:bg-[var(--gold)] hover:text-white transition"
                          title="Grant training"
                          data-testid={`grant-member-${m.id}`}
                        >
                          <Gift size={11} /> Grant
                        </button>
                        <button onClick={() => startEdit(m)} className="p-1 border border-[var(--line)] hover:border-[var(--navy)]" title="Edit" data-testid={`edit-member-${m.id}`}>
                          <Pencil size={12} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {granting && (
        <GrantTrainingDialog
          member={granting}
          products={products}
          grants={grants[granting.email] || []}
          onClose={() => setGranting(null)}
          onChanged={() => { loadGrants(granting.email); onUpdated && onUpdated(); }}
        />
      )}

      {adding && (
        <AddMemberDialog
          onClose={() => setAdding(false)}
          onCreated={() => { setAdding(false); onUpdated && onUpdated(); }}
        />
      )}
    </div>
  );
}

function AddMemberDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "member", organization: "", send_welcome_email: true });
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState(null);

  const genPassword = () => {
    const alphabet = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let s = "";
    for (let i = 0; i < 12; i++) s += alphabet[Math.floor(Math.random() * alphabet.length)];
    setForm((f) => ({ ...f, password: s + "!" }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMsg(null);
    try {
      const { data } = await api.post("/admin/members", {
        email: form.email.trim().toLowerCase(),
        name: form.name.trim(),
        password: form.password,
        role: form.role,
        organization: form.organization.trim(),
        send_welcome_email: form.send_welcome_email,
      });
      setMsg({ type: "ok", text: `Account created for ${data.email}.${form.send_welcome_email ? " Welcome email sent." : " No email sent."}` });
      setTimeout(() => onCreated && onCreated(), 800);
    } catch (err) {
      setMsg({ type: "err", text: formatApiError(err) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white w-full max-w-lg border border-[var(--line)] max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="add-member-dialog"
      >
        <div className="flex items-center justify-between px-5 py-4 bg-[var(--navy)] text-white">
          <div className="flex items-center gap-2">
            <User size={16} className="text-[var(--gold)]" />
            <div>
              <div className="text-[10px] tracking-widest uppercase text-[var(--gold)]">Admin</div>
              <div className="text-sm font-bold">Add Member Manually</div>
            </div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="add-member-close"><X size={16} /></button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs col-span-2">
              <span className="text-[10px] tracking-widest uppercase font-bold text-[var(--navy)]">Full Name *</span>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-[var(--line)] text-sm" data-testid="add-member-name" />
            </label>
            <label className="block text-xs col-span-2">
              <span className="text-[10px] tracking-widest uppercase font-bold text-[var(--navy)]">Email *</span>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-[var(--line)] text-sm" data-testid="add-member-email" />
            </label>
            <label className="block text-xs col-span-2">
              <span className="text-[10px] tracking-widest uppercase font-bold text-[var(--navy)]">Temporary Password *</span>
              <div className="mt-1 flex gap-2">
                <input required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="flex-1 px-3 py-2 border border-[var(--line)] text-sm font-mono" placeholder="min 6 chars" data-testid="add-member-password" />
                <button type="button" onClick={genPassword} className="px-3 py-2 border border-[var(--line)] text-[10px] font-bold uppercase tracking-widest hover:bg-[var(--cream)]" data-testid="add-member-gen-pw">
                  Generate
                </button>
              </div>
            </label>
            <label className="block text-xs">
              <span className="text-[10px] tracking-widest uppercase font-bold text-[var(--navy)]">Role</span>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-[var(--line)] text-sm bg-white" data-testid="add-member-role">
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
            </label>
            <label className="block text-xs">
              <span className="text-[10px] tracking-widest uppercase font-bold text-[var(--navy)]">Organization</span>
              <input value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-[var(--line)] text-sm" data-testid="add-member-org" />
            </label>
          </div>

          <label className="flex items-center gap-2 text-sm text-[var(--navy)]">
            <input type="checkbox" checked={form.send_welcome_email}
              onChange={(e) => setForm({ ...form, send_welcome_email: e.target.checked })}
              data-testid="add-member-send-welcome" />
            Send welcome email with sign-in credentials
          </label>

          {msg && (
            <div className={`text-xs px-3 py-2 border ${msg.type === "ok" ? "bg-green-50 border-green-300 text-green-800" : "bg-red-50 border-red-300 text-red-800"}`}
              data-testid="add-member-msg">{msg.text}</div>
          )}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-[var(--line)] text-sm">Cancel</button>
            <button type="submit" disabled={submitting}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--navy)] text-white text-sm font-bold uppercase tracking-widest disabled:opacity-50"
              data-testid="add-member-submit">
              {submitting ? <Loader2 className="animate-spin" size={14} /> : <UserCheck size={14} />}
              Create Account
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function GrantTrainingDialog({ member, products, grants, onClose, onChanged }) {
  const grantable = useMemo(
    () => products.filter((p) => ["course", "bundle", "toolkit", "download", "membership"].includes(p.type)),
    [products]
  );
  const [productId, setProductId] = useState("");
  const [note, setNote] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState(null);
  const [continuumComp, setContinuumComp] = useState(Boolean(member.continuum_comp));
  const [continuumBusy, setContinuumBusy] = useState(false);
  const [continuumNote, setContinuumNote] = useState(member.continuum_comp_note || "");

  const toggleContinuum = async (next) => {
    setContinuumBusy(true);
    setMsg(null);
    try {
      await api.post(`/admin/members/${encodeURIComponent(member.email)}/continuum-comp`, {
        enabled: next,
        note: next ? (continuumNote || null) : null,
      });
      setContinuumComp(next);
      setMsg({
        type: "ok",
        text: next
          ? `Continuum Portal access granted to ${member.email}.`
          : `Continuum Portal access revoked from ${member.email}.`,
      });
      onChanged && onChanged();
    } catch (err) {
      setMsg({ type: "err", text: formatApiError(err) });
    } finally {
      setContinuumBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!productId) return;
    setSubmitting(true);
    setMsg(null);
    try {
      const { data } = await api.post("/admin/members/grant", {
        email: member.email,
        product_id: productId,
        note: note || null,
        send_email: sendEmail,
      });
      const already = data?.parent?.already;
      setMsg({
        type: "ok",
        text: already
          ? `${data.product} — this member already has access (no new grant needed).`
          : `Granted "${data.product}" to ${member.email}.${sendEmail ? " Receipt email queued." : ""}`,
      });
      setProductId("");
      setNote("");
      onChanged && onChanged();
    } catch (err) {
      setMsg({ type: "err", text: formatApiError(err) });
    } finally {
      setSubmitting(false);
    }
  };

  const revoke = async (pid) => {
    if (!window.confirm("Revoke this comp'd access? The member will lose access immediately.")) return;
    try {
      await api.delete(`/admin/members/${encodeURIComponent(member.email)}/grants/${encodeURIComponent(pid)}`);
      onChanged && onChanged();
      setMsg({ type: "ok", text: "Grant revoked." });
    } catch (err) {
      setMsg({ type: "err", text: formatApiError(err) });
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-[var(--line)]"
        onClick={(e) => e.stopPropagation()}
        data-testid="grant-training-dialog"
      >
        <div className="flex items-center justify-between px-5 py-4 bg-[var(--navy)] text-white">
          <div className="flex items-center gap-2">
            <GraduationCap size={16} className="text-[var(--gold)]" />
            <div>
              <div className="text-[10px] tracking-widest uppercase text-[var(--gold)]">Grant / Comp Training</div>
              <div className="text-sm font-bold">{member.name || member.email}</div>
            </div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="grant-close-btn"><X size={16} /></button>
        </div>

        <div className="border-b border-[var(--line)] px-5 py-4 bg-[var(--cream)]">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] tracking-widest uppercase font-bold text-[var(--navy)] flex items-center gap-1.5">
                <GraduationCap size={12} className="text-[var(--gold)]" />
                Catalyst Continuum Portal Access
              </div>
              <p className="text-[11px] text-[var(--muted)] mt-1 leading-relaxed">
                One-click grant of the private Continuum Portal without any product tie-in. Reversible at any time.
                {continuumComp && member.continuum_comp_by && <span className="block mt-1">Currently comp'd by <strong>{member.continuum_comp_by}</strong>{member.continuum_comp_at ? ` on ${new Date(member.continuum_comp_at).toLocaleDateString()}` : ""}.</span>}
              </p>
            </div>
            <button
              type="button"
              onClick={() => toggleContinuum(!continuumComp)}
              disabled={continuumBusy}
              className={`shrink-0 inline-flex items-center gap-2 px-3 py-2 text-[10px] font-bold uppercase tracking-widest border transition disabled:opacity-50 ${continuumComp ? "bg-red-600 text-white border-red-700 hover:bg-red-700" : "bg-[var(--gold)] text-white border-[var(--gold)] hover:bg-[var(--gold-dark,#b1893e)]"}`}
              data-testid="grant-continuum-toggle"
            >
              {continuumBusy ? <Loader2 className="animate-spin" size={12} /> : (continuumComp ? <X size={12} /> : <CheckCircle2 size={12} />)}
              {continuumComp ? "Revoke Continuum" : "Grant Continuum"}
            </button>
          </div>
          {!continuumComp && (
            <input
              value={continuumNote}
              onChange={(e) => setContinuumNote(e.target.value)}
              placeholder="Optional note (e.g. Launch partner, comp'd for pilot)"
              className="mt-3 w-full px-3 py-2 border border-[var(--line)] text-[12px] bg-white"
              maxLength={500}
              data-testid="grant-continuum-note"
            />
          )}
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="block text-[10px] tracking-widest uppercase font-bold text-[var(--navy)] mb-2">
              Product / Training
            </label>
            <select
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              className="w-full px-3 py-2 border border-[var(--line)] bg-white text-sm"
              required
              data-testid="grant-product-select"
            >
              <option value="">— Select a product —</option>
              {["course", "bundle", "toolkit", "download", "membership"].map((t) => {
                const group = grantable.filter((p) => p.type === t);
                if (!group.length) return null;
                return (
                  <optgroup key={t} label={t.charAt(0).toUpperCase() + t.slice(1) + "s"}>
                    {group.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}{p.price ? ` — $${p.price}` : " — Free"}
                      </option>
                    ))}
                  </optgroup>
                );
              })}
            </select>
            <p className="text-[11px] text-[var(--muted)] mt-1">
              Grants immediate access at $0.00 (no payment). Recorded as a comp with your admin email.
            </p>
          </div>

          <div>
            <label className="block text-[10px] tracking-widest uppercase font-bold text-[var(--navy)] mb-2">
              Note (optional)
            </label>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Complimentary for launch partner"
              className="w-full px-3 py-2 border border-[var(--line)] text-sm"
              maxLength={500}
              data-testid="grant-note-input"
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-[var(--navy)]">
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
              data-testid="grant-send-email"
            />
            Send a complimentary-access notification email to {member.email}
          </label>

          {msg && (
            <div
              className={`text-xs px-3 py-2 border ${msg.type === "ok" ? "bg-green-50 border-green-300 text-green-800" : "bg-red-50 border-red-300 text-red-800"}`}
              data-testid="grant-msg"
            >
              {msg.text}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-[var(--line)] text-sm">
              Close
            </button>
            <button
              type="submit"
              disabled={!productId || submitting}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--gold)] text-white text-sm font-bold uppercase tracking-widest disabled:opacity-50"
              data-testid="grant-submit-btn"
            >
              {submitting ? <Loader2 className="animate-spin" size={14} /> : <Gift size={14} />}
              Grant Access
            </button>
          </div>
        </form>

        {grants.length > 0 && (
          <div className="border-t border-[var(--line)] px-5 py-4 bg-[var(--cream)]">
            <div className="text-[10px] tracking-widest uppercase font-bold text-[var(--navy)] mb-3">
              Current Comps for this Member ({grants.length})
            </div>
            <div className="space-y-2">
              {grants.map((g) => (
                <div key={g.id} className="flex items-center justify-between bg-white border border-[var(--line)] px-3 py-2 text-sm" data-testid={`grant-row-${g.product_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[var(--navy)] truncate">{g.product_name}</div>
                    <div className="text-[11px] text-[var(--muted)]">
                      {g.product_type} · granted {new Date(g.created_at).toLocaleDateString()}
                      {g.metadata?.granted_by ? ` by ${g.metadata.granted_by}` : ""}
                      {g.metadata?.bundle_parent ? " · via bundle" : ""}
                    </div>
                  </div>
                  {!g.metadata?.bundle_parent && (
                    <button
                      onClick={() => revoke(g.product_id)}
                      className="p-1 border border-[var(--line)] hover:border-red-500 hover:text-red-600"
                      title="Revoke"
                      data-testid={`grant-revoke-${g.product_id}`}
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
